/**
 * TrackerHeldNotice — calm one-line amber notice while one tracker's own Flow
 * Optimizer switch holds its scheduler and Apply (Tres Commas: off by
 * default). The backend's reason and what still works sit behind the help
 * icon (the shared Tooltip + HelpCircle hint, as on the drawer's push switch).
 * The all-trackers kill switch keeps its red banner; this never repeats it
 * (useTrackerBlockedReason is null under the global switch, and for Voluum /
 * Binom, which make no request).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { HelpCircle, PauseCircle } from 'lucide-react';
import { trackerLabel, type TrackerId } from '@trafficpulse/shared';

import { Tooltip } from '@/components/ui/Tooltip';
import { heldHint, heldLine } from '@/features/optimizer-center/tracker-status/held-copy';
import { useTrackerBlockedReason } from '@/features/trackers/flow-optimizer';

const NOTICE =
  'flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-4 py-2 text-sm text-warning';
const HINT_BUTTON =
  'inline-flex shrink-0 rounded-full text-warning/80 hover:text-warning focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning/40';
/** Reason on the first line, what still works on the second. */
const HINT = 'max-w-sm whitespace-pre-line';

export const TrackerHeldNotice: FC<{ tracker: TrackerId }> = ({ tracker }) => {
  const reason = useTrackerBlockedReason(tracker);
  if (!reason) return null;
  const label = trackerLabel(tracker);
  return (
    <div role="status" className={NOTICE}>
      <PauseCircle className="h-4 w-4 shrink-0" aria-hidden />
      <p className="min-w-0 truncate font-medium">{heldLine(label)}</p>
      <Tooltip content={heldHint(reason)} side="bottom" interactive contentClassName={HINT}>
        <button type="button" aria-label={`Why ${label} is held`} className={HINT_BUTTON}>
          <HelpCircle className="h-3.5 w-3.5" aria-hidden />
        </button>
      </Tooltip>
    </div>
  );
};

export default TrackerHeldNotice;
